import { useEffect, useMemo, useState } from 'react';
import { useSkillContent } from '../../../apps/apis/commandCenter/hooks';
import { parseSkillManifest, type SkillOutlineEntry } from '../domain/skillManifest';
import { parseInline, type SkillBlock } from '../domain/skillBlocks';

interface SkillFilePanelProps {
  /** The skill whose manifest to show. Cards without one do not render this panel at all. */
  skillId: string;
  /** The node that uploaded it, shown as provenance. */
  ownerNode: string;
}

const sizeOf = (bytes: number): string => (bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`);

/** Renders one line of prose; segments are React nodes, so authored text can never inject markup. */
const Inline = ({ text }: { text: string }) => (
  <>
    {parseInline(text).map((segment, i) => {
      if (segment.kind === 'code') {
        return <code key={i}>{segment.text}</code>;
      }
      if (segment.kind === 'strong') {
        return <strong key={i}>{segment.text}</strong>;
      }
      return <span key={i}>{segment.text}</span>;
    })}
  </>
);

const Block = ({ block }: { block: SkillBlock }) => {
  switch (block.kind) {
    case 'heading': {
      const Tag = `h${Math.min(block.level, 4)}` as 'h1' | 'h2' | 'h3' | 'h4';
      return (
        <Tag id={block.id} className="sk-h">
          {block.number ? <span className="sk-num">{block.number}</span> : null}
          <Inline text={block.text} />
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p>
          <Inline text={block.text} />
        </p>
      );
    case 'list': {
      const items = block.items.map((item, i) => (
        <li key={i}>
          <Inline text={item} />
        </li>
      ));
      return block.ordered ? <ol>{items}</ol> : <ul>{items}</ul>;
    }
    case 'code':
      return (
        <pre className="sk-pre">
          <code>{block.text}</code>
        </pre>
      );
    case 'table':
      return (
        <div className="sk-tablewrap">
          <table className="sk-table">
            <thead>
              <tr>
                {block.head.map((cell, i) => (
                  <th key={i}>
                    <Inline text={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'quote':
      return (
        <blockquote>
          <Inline text={block.text} />
        </blockquote>
      );
    default:
      return <hr />;
  }
};

const OutlineLink = ({ entry, onOpen }: { entry: SkillOutlineEntry; onOpen: (id: string) => void }) => (
  <li className={`sk-sp sk-sp${entry.level}`}>
    <button type="button" onClick={() => onOpen(entry.id)}>
      {entry.number ? <span className="sk-sp-n">{entry.number}</span> : null}
      <span className="sk-sp-t">{entry.text}</span>
    </button>
  </li>
);

/**
 * Shows the SKILL.md an agent wrote for itself. Collapsed it leads with the description alone, so a
 * card costs a few lines on the board; the section rail belongs to the opened document. Read-only by
 * design: the file belongs to the authoring agent, and edits happen in Slack.
 */
const SkillFilePanel = ({ skillId, ownerNode }: SkillFilePanelProps) => {
  const [open, setOpen] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const { data, isLoading, isError } = useSkillContent(skillId);
  const parsed = useMemo(() => (data ? parseSkillManifest(data.content) : null), [data]);

  // The heading only exists once the body has rendered, so the scroll waits for that commit.
  useEffect(() => {
    if (!open || pendingAnchor === null) {
      return;
    }
    document.getElementById(pendingAnchor)?.scrollIntoView?.({ block: 'start' });
    setPendingAnchor(null);
  }, [open, pendingAnchor]);

  return (
    <section className="panel sk-panel" data-open={open} data-testid="skill-file-panel">
      <div className="panel-h">
        <span className="t">Skill file</span>
        {parsed && data ? (
          <span className="sk-meta">
            {parsed.name ?? skillId}
            {parsed.version ? ` v${parsed.version}` : ''} · {sizeOf(data.bytes)} · from {ownerNode}
          </span>
        ) : null}
        <div className="sk-actions">
          <button type="button" className="btn" onClick={() => setOpen((wasOpen) => !wasOpen)} aria-expanded={open}>
            {open ? 'Hide the file' : 'Read the full file'}
          </button>
        </div>
      </div>

      <div className="sk-head">
        {isLoading ? <div className="sk-note">Loading the file…</div> : null}
        {isError ? <div className="sk-note">This skill has no file on the control plane yet.</div> : null}
        {parsed ? (
          <>
            {parsed.description ? <p className="sk-desc">{parsed.description}</p> : null}
            <div className="sk-author">
              Written by <b>{parsed.author ?? 'nobody recorded'}</b>
            </div>
          </>
        ) : null}
      </div>

      {open && parsed ? (
        <div className="sk-body">
          <nav className="sk-spine" aria-label="Sections of this skill">
            <div className="sk-spine-l">What it does</div>
            <ol>
              {parsed.outline.map((entry) => (
                <OutlineLink key={entry.id} entry={entry} onOpen={setPendingAnchor} />
              ))}
            </ol>
          </nav>
          <article className="sk-doc">
            {parsed.outline.length === 0 ? (
              <div className="sk-note">This file is short enough to read without sections.</div>
            ) : null}
            {parsed.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </article>
        </div>
      ) : null}
    </section>
  );
};

export default SkillFilePanel;
